/**
 * LE REPÈRE DE TÊTE — la rangée de tuiles chiffrées qui ouvre un écran de liste.
 *
 * POURQUOI IL EXISTE. Sept écrans du back-office (stock, mouvements, écarts, à valider, clients,
 * titres, marketing) portaient EXACTEMENT la même tuile, recopiée sept fois avec ses hex de repli en
 * dur. Sept copies d'une géométrie ne divergent pas d'un coup : elles dérivent d'un demi-pixel à
 * chaque écran neuf, et personne ne voit laquelle est la bonne.
 *
 * Valeurs relevées VERBATIM sur la maquette (`apps/backoffice/design/*.dc.html`, blocs
 * `*Kpis`) — c'est le relevé qui fait foi, jamais une valeur ronde qu'on trouverait plus jolie.
 *
 * ⚠️ CE N'EST PAS UNE CARTE DE TABLEAU DE BORD. Un repère de tête tient sur une ligne, sans
 * graphique et sans variation : il répond « combien » d'un coup d'œil avant qu'on lise la liste qui
 * suit. Ce qui demande une courbe ou une comparaison va dans `chart`, pas ici.
 */

/** Anatomie de la rangée et de la tuile — lue par `ui-web/kpi`. */
export const KPI_TILE = {
  /** La grille : autant de tuiles que la largeur en porte, jamais un nombre de colonnes figé. */
  gridMin: 185,
  gridGap: 8,
  rowGap: 10,
  padding: '8px 12px',
  gap: 10,
  /** Le carré d'icône teinté par le ton. */
  square: 30,
  iconSize: 15,
  iconStroke: 1.9,
  labelSize: 10,
  labelWeight: 700,
  labelSpacing: '.05em',
  valueSize: 14.5,
  valueWeight: 800,
  /** Graisse et encre du « à venir » — voir `ui-web/kpi` pour la règle. */
  soonWeight: 600,
} as const;

/**
 * TONS DE LA TUILE — la couleur dit DE QUOI on parle (une population, de l'argent, un envoi),
 * jamais si la nouvelle est bonne. Un repère à 0 se peint comme un repère à 10 000.
 *
 * ⚠️ LISTE FERMÉE, ET DISTINCTE DE `MODAL_TILE_TONES` À DESSEIN. Les deux teintent un carré d'icône,
 * mais la maquette leur donne deux encres différentes sur le même fond `teal-100` : `teal-800` ici,
 * `teal-700` dans la modale. Ce sont DEUX RELEVÉS, pas deux inventions — les fondre en un seul
 * décalerait l'un des deux composants sans que personne l'ait décidé.
 */
export const KPI_TILE_TONES = {
  teal: { bg: 'teal-100', fg: 'teal-800' },
  blue: { bg: 'blue-bg', fg: 'blue-fg' },
  violet: { bg: 'violet-bg', fg: 'violet-fg' },
  amber: { bg: 'amber-bg', fg: 'amber-fg' },
  rose: { bg: 'rose-bg', fg: 'rose-fg' },
  /** Neutre — le repli quand la mesure n'appelle aucune famille de couleur. */
  ink: { bg: 'ink-100', fg: 'ink-600' },
} as const;

export type KpiTone = keyof typeof KPI_TILE_TONES;
